import {autoserializeAs, inheritSerialization} from "dcerialize";
import {SimpleBactoforceLocation} from "../bactoforce/BactoforceLocation";
import type {Existing, IdentifiableModel, IdOf, New} from "../base/IdentifiableModel";
import {createExisting, createNew} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import {IdOnlyCustomerDepartment, SimpleCustomerDepartment} from "../customers/CustomerDepartment";
import {IdOnlyCustomerLocation, SimpleCustomerLocation} from "../customers/CustomerLocation";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyReportService, SimpleReportService} from "../reports/ReportService";
import {IdOnlyUser, SimpleUser} from "./User";

// region Bactoforce Users
/**
 * @sealed
 */
export class BactoforceLocationUserAssignment implements ModificationTrackedModel {
  @autoserializeAs(() => Boolean)
  public readonly isPrimary: boolean;
  @autoserializeAs(() => SimpleBactoforceLocation)
  public readonly location: Readonly<Existing<SimpleBactoforceLocation>>;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

// endregion

// region Customer Users
export class IdOnlyCustomerUserLocationAssignment implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyCustomerUserLocationAssignment>): Existing<IdOnlyCustomerUserLocationAssignment> {
    return createExisting(IdOnlyCustomerUserLocationAssignment, id);
  }
}

@inheritSerialization(() => IdOnlyCustomerUserLocationAssignment)
export class WritableCustomerUserLocationAssignment extends IdOnlyCustomerUserLocationAssignment {
  @autoserializeAs(() => IdOnlyCustomerLocation)
  public customerLocation: Existing<IdOnlyCustomerLocation>;
  @autoserializeAs(() => IdOnlyCustomerDepartment)
  public customerDepartment?: Existing<IdOnlyCustomerDepartment>;

  public static createNew(
    customerLocation: Readonly<Existing<IdOnlyCustomerLocation>>,
    customerDepartment?: Readonly<Existing<IdOnlyCustomerDepartment>>,
  ): New<WritableCustomerUserLocationAssignment> {
    const assignment = createNew(WritableCustomerUserLocationAssignment);
    assignment.customerLocation = customerLocation;
    assignment.customerDepartment = customerDepartment;
    return assignment;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableCustomerUserLocationAssignment)
export class CustomerUserLocationAssignment extends WritableCustomerUserLocationAssignment implements ModificationTrackedModel {
  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  public static get allDepartmentsText(): string {
    return $localize`:@@customer-location-user-assignment.all-departments:All`;
  }

  @autoserializeAs(() => SimpleCustomerLocation)
  public override customerLocation: Readonly<Existing<SimpleCustomerLocation>>;
  @autoserializeAs(() => SimpleCustomerDepartment)
  public override customerDepartment?: Readonly<Existing<SimpleCustomerDepartment>>;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;

  public get departmentName(): string | null {
    return this.customerDepartment?.name ?? CustomerUserLocationAssignment.allDepartmentsText;
  }
}

// endregion

// region Third Party Users
export class IdOnlyThirdPartyUserCustomerAssignment {
  @autoserializeAs(() => IdOnlyUser)
  public user: Existing<IdOnlyUser>;
  @autoserializeAs(() => IdOnlyCustomerLocation)
  public customerLocation: Existing<IdOnlyCustomerLocation>;

  public get composedId(): string {
    return `${this.user.id}-${this.customerLocation.id}`;
  }

  public constructor(
    user: Readonly<Existing<IdOnlyUser>>,
    customerLocation: Readonly<Existing<IdOnlyCustomerLocation>>,
  ) {
    this.user = user;
    this.customerLocation = customerLocation;
  }
}

@inheritSerialization(() => IdOnlyThirdPartyUserCustomerAssignment)
export class WritableThirdPartyUserCustomerAssignment extends IdOnlyThirdPartyUserCustomerAssignment {
  @autoserializeAs(() => IdOnlyReportService)
  public reportService: Existing<IdOnlyReportService>;

  public constructor(
    user: Readonly<Existing<IdOnlyUser>>,
    customerLocation: Readonly<Existing<IdOnlyCustomerLocation>>,
    reportService: Readonly<Existing<IdOnlyReportService>>,
  ) {
    super(user, customerLocation);
    this.reportService = reportService;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableThirdPartyUserCustomerAssignment)
export class ThirdPartyUserCustomerAssignment extends WritableThirdPartyUserCustomerAssignment implements ModificationTrackedModel {
  @autoserializeAs(() => SimpleUser)
  public override user: Readonly<Existing<SimpleUser>>;
  @autoserializeAs(() => SimpleCustomerLocation)
  public override customerLocation: Readonly<Existing<SimpleCustomerLocation>>;
  @autoserializeAs(() => SimpleReportService)
  public override reportService: Readonly<Existing<SimpleReportService>>;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

// endregion
